<template>
  <div class="contract-request-list">
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="'Danh sách Đề nghị tạo HĐ'">
          <template v-slot:toolbar>
            <div class="row">
              <div class="ml-3">
                <b-button size="sm" variant="primary" @click="goCreate">
                  <i style="font-size: 1rem" class="flaticon2-add-1"></i>
                  <b>Tạo Đề nghị</b>
                </b-button>
              </div>
            </div>
          </template>

          <template v-slot:preview>
            <!-- ===== Stats cards (4 ô) ===== -->
            <div class="cr-stats">
              <div
                v-for="s in statsCards"
                :key="s.key"
                class="cr-stat-card"
                :class="{ active: filter.status === s.status }"
                @click="onSelectStat(s.status)"
              >
                <div
                  class="cr-stat-num"
                  :style="{ background: s.bg, color: s.color }"
                >
                  {{
                    statsCounts[s.key] !== undefined ? statsCounts[s.key] : '—'
                  }}
                </div>
                <span class="cr-stat-label">{{ s.label }}</span>
              </div>
            </div>

            <!-- ===== Filter card ===== -->
            <div class="cr-filter-card">
              <b-row>
                <b-col cols="12" md="3">
                  <label class="cr-label">Tìm kiếm</label>
                  <b-form-input
                    size="sm"
                    placeholder="Tên KH, SĐT, mã HĐ..."
                    v-model="filter.customerName"
                    autocomplete="off"
                    v-on:keyup.enter="onFilter"
                  />
                </b-col>
                <b-col cols="12" md="3">
                  <label class="cr-label">Khoảng ngày tạo</label>
                  <div class="cr-daterange">
                    <date-picker
                      placeholder="Từ ngày"
                      class="form-control form-control-sm"
                      :config="dpConfigs.date"
                      v-model="filter.fromDate"
                    ></date-picker>
                    <date-picker
                      placeholder="Đến ngày"
                      class="form-control form-control-sm"
                      :config="dpConfigs.date"
                      v-model="filter.toDate"
                    ></date-picker>
                  </div>
                </b-col>
                <b-col cols="6" md="2">
                  <label class="cr-label">Trạng thái</label>
                  <b-form-select
                    size="sm"
                    v-model="filter.status"
                    :options="bvStatusOptions"
                    class="select-style"
                  />
                </b-col>
                <b-col cols="6" md="2">
                  <label class="cr-label">Cửa hàng</label>
                  <b-form-select
                    size="sm"
                    v-model="filter.storeId"
                    :options="bvStoreOptions"
                    class="select-style"
                  />
                </b-col>
                <b-col cols="12" md="2" class="d-flex align-items-end">
                  <b-button block variant="primary" size="sm" @click="onFilter">
                    <i class="flaticon2-magnifier-tool"></i>
                    Lọc
                  </b-button>
                </b-col>
              </b-row>
            </div>

            <!-- ===== Table — copy pattern view/components/contracts/List.vue ===== -->
            <b-row>
              <b-col cols="12">
                <table class="table table-bordered-list table-hover col-md-12">
                  <thead>
                    <tr>
                      <th
                        class="text-center header-table-color"
                        style="width: 36px"
                      >
                        <b-form-checkbox
                          :checked="allSelectedOnPage"
                          :indeterminate="someSelectedOnPage"
                          size="lg"
                          @change="toggleSelectAll"
                        />
                      </th>
                      <th class="text-center header-table-color">Mã HĐ</th>
                      <th
                        class="text-center header-table-color"
                        style="width: 15%"
                      >
                        Khách hàng
                      </th>
                      <th
                        class="text-center header-table-color"
                        style="width: 20%"
                      >
                        Sản phẩm
                      </th>
                      <th class="text-center header-table-color">Giá</th>
                      <th class="text-center header-table-color">Chiết khấu</th>
                      <th class="text-center header-table-color">SL</th>
                      <th class="text-center">Tổng tiền</th>
                      <th class="text-center header-table-color">Duyệt</th>
                      <th></th>
                    </tr>
                  </thead>
                  <!-- 1 <tbody> per request → CSS `tbody:hover tr` highlight cả nhóm row
                       (cell rowspan + items) khi hover bất kỳ row nào của request đó. -->
                  <tbody
                    v-for="(req, i) in items"
                    :key="'tbody-' + (req.id || i)"
                    class="cr-req-group"
                  >
                    <template>
                      <!-- Metadata sub-row (Ngày tạo / Cập nhật / Người Tạo) -->
                      <tr :key="'meta-' + (req.id || i)">
                        <td
                          colspan="10"
                          style="
                            padding: 5px !important;
                            border-bottom: none !important;
                          "
                        >
                          <div class="d-flex" style="font-size: 0.87rem">
                            <span class="mr-4">
                              Ngày tạo:
                              <span class="text-primary">
                                {{ formatDate(req.createdAt) }}
                              </span>
                            </span>
                            <span
                              class="mr-4"
                              v-if="req.createdAt !== req.updatedAt"
                            >
                              Cập nhật:
                              <span class="text-primary">
                                {{ formatDate(req.updatedAt) }}
                              </span>
                            </span>
                            <span class="mr-4" v-if="getCreatorName(req)">
                              Người Tạo:
                              <span class="text-primary">{{
                                getCreatorName(req)
                              }}</span>
                            </span>
                            <span class="mr-4" v-if="getSaleName(req)">
                              TVBH:
                              <span class="text-primary">{{
                                getSaleName(req)
                              }}</span>
                            </span>
                          </div>
                        </td>
                      </tr>

                      <!-- Item rows với rowspan -->
                      <template v-for="(it, j) in normalizedItems(req)">
                        <tr :key="'row-' + (req.id || i) + '-' + j">
                          <!-- Checkbox -->
                          <td
                            v-if="j === 0"
                            :rowspan="normalizedItems(req).length"
                            class="tdCenter"
                          >
                            <b-form-checkbox
                              size="lg"
                              :checked="selected.includes(req.id)"
                              class="text-center ml-1"
                              @change="toggleSelect(req.id)"
                            />
                          </td>

                          <!-- Mã HĐ -->
                          <td
                            v-if="j === 0"
                            :rowspan="normalizedItems(req).length"
                            class="tdCenter text-center"
                          >
                            <div
                              class="orderCode text-primary font-weight-bold"
                              @click="onEdit(req)"
                            >
                              {{ req.code || '—' }}
                            </div>
                          </td>

                          <!-- Khách hàng -->
                          <td
                            v-if="j === 0"
                            :rowspan="normalizedItems(req).length"
                            class="tdCenter"
                          >
                            <small style="color: #fd9d9d">
                              ({{
                                isBusiness(req) ? 'Doanh nghiệp' : 'Khách lẻ'
                              }})
                            </small>
                            <p
                              class="orderCode mb-0"
                              @click="onEdit(req)"
                              style="font-weight: 550"
                            >
                              {{ req.customerName || '—' }}
                            </p>
                            <p
                              v-if="req.customerPhone"
                              class="orderCode mb-0"
                              @click="onEdit(req)"
                            >
                              {{ req.customerPhone }}
                            </p>
                            <div
                              v-if="
                                req.customerAddress &&
                                req.customerAddress.trim().length > 0
                              "
                            >
                              <b>ĐC:</b>
                              {{ req.customerAddress }}
                            </div>
                          </td>

                          <!-- Sản phẩm (1 row/item) -->
                          <td class="align-middle">
                            <template v-if="it._placeholder">
                              <p class="m-0 text-muted">—</p>
                            </template>
                            <template v-else>
                              <p
                                v-if="getProductCode(it)"
                                class="m-0 text-muted"
                                style="font-size: 12px"
                              >
                                {{ getProductCode(it) }}
                              </p>
                              <p class="m-0">
                                <span
                                  v-if="it._lineKind === 'GIFT'"
                                  class="label label-inline label-light-primary mr-1"
                                  >Quà tặng</span
                                >
                                <span
                                  v-else-if="it._lineKind === 'ATTACH'"
                                  class="label label-inline label-light-dark mr-1"
                                  >Bán kèm</span
                                >
                                {{ it.productName }}
                              </p>
                              <p v-if="it.productChassis" class="mb-0">
                                <i class="text-primary" style="font-size: 12px">
                                  SK: {{ it.productChassis.chassisNumber }}
                                </i>
                                <br />
                                <i class="text-primary" style="font-size: 12px">
                                  SM: {{ it.productChassis.engineNumber }}
                                </i>
                              </p>
                              <p v-if="it.productSerial" class="mb-0">
                                <i class="text-primary" style="font-size: 12px">
                                  SERI: {{ it.productSerial.serialNumber }}
                                </i>
                              </p>
                            </template>
                          </td>

                          <!-- Giá -->
                          <td class="text-right align-middle">
                            <template v-if="!it._placeholder">
                              {{ formatPrice(it.productPrice) }}
                            </template>
                          </td>

                          <!-- Chiết khấu (per-item) — pattern theo ListBill -->
                          <td class="text-right align-middle">
                            <template v-if="!it._placeholder">
                              <span
                                v-if="
                                  it.discountType === 'AMOUNT' &&
                                  Number(it.discountAmount) > 0
                                "
                                class="text-warning font-weight-bold"
                              >
                                {{ formatPrice(it.discountAmount) }}
                              </span>
                              <span
                                v-else-if="
                                  it.discountType === 'PERCENT' &&
                                  Number(it.discountAmount) > 0
                                "
                                class="text-warning font-weight-bold"
                              >
                                {{ Number(it.discountAmount) }}%
                              </span>
                            </template>
                          </td>

                          <!-- SL -->
                          <td class="text-center align-middle">
                            <template v-if="!it._placeholder">
                              {{ it.quantity || 0 }}
                            </template>
                          </td>

                          <!-- Tổng tiền (rowspan) — hiện discount line nếu có -->
                          <td
                            v-if="j === 0"
                            :rowspan="normalizedItems(req).length"
                            class="text-right align-middle"
                          >
                            <p
                              v-if="getRequestDiscountValue(req) > 0"
                              class="border-0 mb-0"
                              v-b-tooltip.hover
                              title="Chiết khấu hợp đồng"
                            >
                              <i
                                class="fa fa-arrow-down icon-color"
                                style="font-size: 12px"
                              ></i>
                              :
                              <span class="text-warning">
                                {{ formatPrice(getRequestDiscountValue(req)) }}
                                <template v-if="req.discountType === 'PERCENT'">
                                  ({{ Number(req.totalDiscountAmount) }}%)
                                </template>
                              </span>
                            </p>
                            <b style="color: #2e7d32">
                              {{ formatPrice(getRequestTotal(req)) }}
                            </b>
                          </td>

                          <!-- Duyệt (rowspan) -->
                          <td
                            v-if="j === 0"
                            :rowspan="normalizedItems(req).length"
                            class="align-middle text-center"
                          >
                            <!-- 2026-05-13 update: bỏ hardcode text "Đã duyệt" cho mọi
                                 req có `approvedAt` — sai cho contract_created / rejected /
                                 cancelled_manual vì các status này cũng có approvedAt. Giờ
                                 luôn show status badge (đúng status hiện tại) + approvedAt
                                 làm subtext nhỏ phía dưới. Vẫn ưu tiên approve button khi
                                 user có quyền duyệt và req đang pending. -->
                            <template v-if="canApprove(req)">
                              <div
                                style="cursor: pointer"
                                @click="onApprove(req)"
                                class="d-flex justify-content-center align-items-center"
                              >
                                <v-icon
                                  small
                                  style="font-size: 13px"
                                  v-b-tooltip
                                  title="Duyệt đề nghị"
                                  >menu-icon flaticon2-checking</v-icon
                                >
                              </div>
                            </template>
                            <template v-else>
                              <span
                                class="cr-status-badge"
                                :style="statusBadgeStyle(req.status)"
                              >
                                <i
                                  :class="[
                                    'cr-status-icon',
                                    statusIcon(req.status),
                                  ]"
                                ></i>
                                {{ statusLabel(req.status) }}
                              </span>
                              <p
                                v-if="req.approvedAt"
                                class="text-center text-muted mb-0 mt-1"
                                style="font-size: 11px"
                              >
                                {{ formatDateOnly(req.approvedAt) }}
                                {{ formatTimeOnly(req.approvedAt) }}
                              </p>
                            </template>
                          </td>

                          <!-- Action gear dropdown (rowspan) -->
                          <td
                            v-if="j === 0"
                            :rowspan="normalizedItems(req).length"
                            class="align-middle"
                          >
                            <div class="d-flex justify-content-center">
                              <b-dropdown size="sm" no-caret right>
                                <template slot="button-content">
                                  <i
                                    style="font-size: 1rem; padding-right: 0px"
                                    class="fa-solid fa-gear"
                                  ></i>
                                </template>
                                <b-dropdown-item @click="onEdit(req)">
                                  <span style="color: #3f4254; font-size: 12px">
                                    <i
                                      style="font-size: 1rem"
                                      class="flaticon2-pen icon-color"
                                    ></i>
                                    &nbsp; Chi tiết / Sửa
                                  </span>
                                </b-dropdown-item>
                                <b-dropdown-item
                                  v-if="canApprove(req)"
                                  @click="onApprove(req)"
                                >
                                  <span style="color: #3f4254; font-size: 12px">
                                    <i
                                      style="font-size: 1rem; color: #1e9c1e"
                                      class="fas fa-check-circle"
                                    ></i>
                                    &nbsp; Duyệt
                                  </span>
                                </b-dropdown-item>
                                <b-dropdown-item
                                  v-if="canReject(req)"
                                  @click="onReject(req)"
                                >
                                  <span style="color: #3f4254; font-size: 12px">
                                    <i
                                      style="font-size: 1rem; color: #d33"
                                      class="fas fa-times-circle"
                                    ></i>
                                    &nbsp; Từ chối
                                  </span>
                                </b-dropdown-item>
                                <b-dropdown-item
                                  v-if="canConvert(req)"
                                  @click="onConvert(req)"
                                >
                                  <span style="color: #3f4254; font-size: 12px">
                                    <i
                                      style="font-size: 1rem; color: #1976d2"
                                      class="flaticon2-file"
                                    ></i>
                                    &nbsp; Tạo HĐ
                                  </span>
                                </b-dropdown-item>
                                <b-dropdown-item
                                  v-if="canCancel(req)"
                                  @click="onCancel(req)"
                                >
                                  <span style="color: #3f4254; font-size: 12px">
                                    <i class="fas fa-ban"></i>
                                    &nbsp; Hủy Đề nghị
                                  </span>
                                </b-dropdown-item>
                              </b-dropdown>
                            </div>
                          </td>
                        </tr>
                      </template>
                    </template>
                  </tbody>

                  <!-- Loading state — separate tbody -->
                  <tbody v-if="loading">
                    <tr>
                      <td colspan="10">
                        <vcl-table :speed="5" :animate="true" :columns="10" />
                      </td>
                    </tr>
                  </tbody>

                  <!-- Empty state — separate tbody -->
                  <tbody v-else-if="!items || !items.length">
                    <tr>
                      <td colspan="10" class="text-center text-muted py-4">
                        Không có đề nghị nào.
                      </td>
                    </tr>
                  </tbody>

                  <!-- Sum row tbody — 10 cột:
                       ☐ | Mã HĐ | KH | SP | Giá | CK | SL | Tổng | Duyệt | Action -->
                  <tbody v-if="items && items.length" class="cr-sum-tbody">
                    <tr>
                      <td
                        :colspan="3"
                        class="align-middle text-right icon-color font-weight-bolder"
                      >
                        Tổng
                      </td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td
                        class="align-middle text-right icon-color font-weight-bolder"
                      >
                        {{ pageQuantity }}
                      </td>
                      <td
                        class="align-middle text-right icon-color font-weight-bolder"
                      >
                        <b style="color: #2e7d32">
                          {{ formatPrice(pageTotal) }}
                        </b>
                      </td>
                      <td colspan="2"></td>
                    </tr>
                  </tbody>
                </table>
              </b-col>
            </b-row>

            <!-- ===== Pagination ===== -->
            <b-row>
              <b-col>
                <p class="mt-3 text-dark" style="font-weight: 500">
                  Tổng số: {{ total }}
                </p>
              </b-col>
              <div class="d-flex justify-content-end">
                <b-col>
                  <b-pagination-nav
                    class="custom-pagination"
                    v-if="numberOfPage >= 2"
                    :link-gen="linkGen"
                    :number-of-pages="numberOfPage"
                    use-router
                    @change="onPageChange"
                    align="right"
                    first-class="page-item-first btn btn-icon btn-sm mr-1 my-1"
                    prev-class="page-item-prev btn btn-icon btn-sm mr-.5 my-1"
                    next-class="page-item-next btn btn-icon btn-sm mr-1 my-1 ml-.5"
                    last-class="page-item-last btn btn-icon btn-sm my-1 "
                    page-class="btn btn-icon btn-sm border-0 mr-.5 my-1"
                  >
                    <template v-slot:first-text>
                      <i class="ki ki-bold-double-arrow-back icon-xs"></i>
                    </template>
                    <template v-slot:prev-text>
                      <i class="ki ki-bold-arrow-back icon-xs"></i>
                    </template>
                    <template v-slot:next-text>
                      <i class="ki ki-bold-arrow-next icon-xs"></i>
                    </template>
                    <template v-slot:last-text>
                      <i class="ki ki-bold-double-arrow-next icon-xs"></i>
                    </template>
                  </b-pagination-nav>
                </b-col>
              </div>
            </b-row>
          </template>
        </KTCodePreview>
      </div>
    </div>

    <!-- ===== Floating bulk action bar ===== -->
    <transition name="cr-bulk-fade">
      <div v-if="selected.length > 0" class="cr-bulk-bar">
        <span class="cr-bulk-count">Đã chọn {{ selected.length }}</span>
        <b-button
          v-if="bulkApproveEnabled"
          variant="success"
          size="sm"
          :disabled="bulkRunning"
          @click="bulkApprove"
        >
          <i class="fas fa-check mr-1"></i>
          Duyệt
        </b-button>
        <b-button variant="light" size="sm" @click="clearSelection">
          Bỏ chọn
        </b-button>
      </div>
    </transition>

    <!-- Modal: Reason (reject / cancel) -->
    <b-modal
      v-model="reasonDialog.show"
      :title="reasonDialog.title"
      hide-footer
      no-close-on-backdrop
    >
      <b-form-group>
        <label>Lý do (tối thiểu 10 ký tự) <b class="text-danger">*</b>:</label>
        <b-form-textarea
          v-model="reasonDialog.reason"
          size="sm"
          :rows="3"
          :max-rows="5"
          placeholder="Nhập lý do"
          :state="reasonDialog.error ? false : null"
        />
        <b-form-invalid-feedback :state="!reasonDialog.error">
          {{ reasonDialog.error }}
        </b-form-invalid-feedback>
      </b-form-group>
      <div class="d-flex justify-content-end mt-3">
        <b-button
          class="mr-2"
          size="sm"
          variant="secondary"
          @click="closeReasonDialog"
        >
          Đóng
        </b-button>
        <b-button
          size="sm"
          variant="primary"
          :disabled="reasonDialog.submitting"
          @click="submitReasonDialog"
        >
          Xác nhận
        </b-button>
      </div>
    </b-modal>

    <!-- Modal: Convert to HĐ -->
    <b-modal
      v-model="convertDialog.show"
      title="Tạo HĐ từ Đề nghị"
      hide-footer
      no-close-on-backdrop
    >
      <p class="text-muted small mb-3">
        Đề nghị đã được duyệt. Hệ thống sẽ tạo Hợp đồng dựa trên thông tin đã
        chốt — không cần nhập lại pháp lý KH.
      </p>

      <b-form-group>
        <label>Mã hợp đồng <b class="text-danger">*</b>:</label>
        <b-form-input
          v-model="convertDialog.contractNumber"
          size="sm"
          placeholder="Nhập mã hợp đồng"
          :state="convertDialog.errors.contractNumber ? false : null"
        />
        <b-form-invalid-feedback :state="!convertDialog.errors.contractNumber">
          {{ convertDialog.errors.contractNumber }}
        </b-form-invalid-feedback>
      </b-form-group>

      <!-- 2026-05-14: bỏ 2 field "Ngân hàng" + "Số tiền vay" cho INSTALLMENT
           theo yêu cầu user. BE đã relax validation (không còn bắt buộc).
           Nếu sau này cần ghi nhận lại, restore template block + body sending. -->

      <b-alert v-if="convertDialog.errorMessage" variant="danger" show>
        {{ convertDialog.errorMessage }}
      </b-alert>

      <div class="d-flex justify-content-end mt-3">
        <b-button
          class="mr-2"
          size="sm"
          variant="secondary"
          :disabled="convertDialog.submitting"
          @click="closeConvertDialog"
        >
          Đóng
        </b-button>
        <b-button
          size="sm"
          variant="primary"
          :disabled="convertDialog.submitting"
          @click="submitConvertDialog"
        >
          Tạo HĐ
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { get } from 'lodash';
import moment from 'moment';
import { VclTable } from 'vue-content-loading';

import KTCodePreview from '@/view/content/CodePreview.vue';
import ContractRequestService from '@/core/services/contractRequest.service';
import ContractService from '@/core/services/contract.service';
import ApiService from '@/core/services/api.service';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import {
  CONTRACT_REQUEST_STATUS,
  CONTRACT_REQUEST_STATUS_LABEL,
  CONTRACT_REQUEST_STATUS_OPTIONS,
} from '@/core/constants/contractRequest';
import {
  makeToastSuccess,
  makeToastFaile,
  convertPrice,
  calTotalDiscount,
} from '@/utils/common';

const REASON_ACTION = {
  REJECT: 'reject',
  CANCEL: 'cancel',
};

// Status icon + color map — đồng bộ với STATS_CARDS để badge ở row và card ở
// dashboard cùng tông. FA icons thay emoji (DEC-32).
//  2026-05-13 update — chỉnh màu 2 status user feedback "xám khó nhìn":
//    - cancelled_manual: #80808F (xám nhạt) → #3F4254 trên #E4E6EF (Metronic
//      dark text, đậm hơn, vẫn neutral để khác red của "Từ chối").
//    - contract_created: #3699FF (xanh) → #8950FC trên #EEE5FF (purple, đồng
//      bộ với stats card cùng status — purple = "đã hoàn tất chu trình").
const STATUS_INFO_MAP = {
  pending: { icon: 'fas fa-hourglass-half', bg: '#FFF4DE', color: '#FFA800' },
  approved: { icon: 'fas fa-check-circle', bg: '#C9F7F5', color: '#1BC5BD' },
  rejected: { icon: 'fas fa-times-circle', bg: '#FFE2E5', color: '#F64E60' },
  contract_created: {
    icon: 'fas fa-file-alt',
    bg: '#EEE5FF',
    color: '#8950FC',
  },
  cancelled_manual: { icon: 'fas fa-ban', bg: '#E4E6EF', color: '#3F4254' },
};

// Stats cards — click để filter nhanh theo status. Màu sắc khớp STATUS_INFO_MAP
// để badge ở row và card ở dashboard cùng tông.
const STATS_CARDS = [
  { key: 'all', label: 'Tổng', status: null, bg: '#E1E9FF', color: '#3699FF' },
  {
    key: 'pending',
    label: 'Chờ duyệt',
    status: CONTRACT_REQUEST_STATUS.PENDING,
    bg: '#FFF4DE',
    color: '#FFA800',
  },
  {
    key: 'approved',
    label: 'Đã duyệt',
    status: CONTRACT_REQUEST_STATUS.APPROVED,
    bg: '#C9F7F5',
    color: '#1BC5BD',
  },
  {
    key: 'contract_created',
    label: 'Đã tạo HĐMB',
    status: CONTRACT_REQUEST_STATUS.CONTRACT_CREATED,
    bg: '#EEE5FF',
    color: '#8950FC',
  },
  {
    key: 'rejected',
    label: 'Từ chối',
    status: CONTRACT_REQUEST_STATUS.REJECTED,
    bg: '#FFE2E5',
    color: '#F64E60',
  },
  {
    key: 'cancelled_manual',
    label: 'Đã hủy',
    status: CONTRACT_REQUEST_STATUS.CANCELLED_MANUAL,
    bg: '#F3F6F9',
    color: '#80808F',
  },
];

export default {
  name: 'ContractRequestList',
  components: { KTCodePreview, VclTable },
  data() {
    return {
      loading: false,
      items: [],
      total: 0,
      page: parseInt(this.$route.query.page) || 1,
      size: 10,
      filter: {
        status: null,
        storeId: null,
        customerName: '',
        // Giới hạn khoảng thời gian (theo Ngày tạo) để tránh load full data —
        // mặc định 1 tháng gần nhất.
        fromDate: moment().subtract(1, 'months').format('DD/MM/YYYY'),
        toDate: moment().format('DD/MM/YYYY'),
      },
      // Config date-picker (vue-bootstrap-datetimepicker) — đồng bộ bills/transaction.
      dpConfigs: {
        date: {
          format: 'DD/MM/YYYY',
          useCurrent: false,
          showClear: true,
          showClose: true,
          locale: 'vi',
        },
      },
      storeOptions: [],
      statusOptions: CONTRACT_REQUEST_STATUS_OPTIONS,
      // Stats counts theo status — load song song với list (4 calls limit=1).
      statsCounts: {
        all: undefined,
        pending: undefined,
        approved: undefined,
        contract_created: undefined,
      },
      // Bulk select state
      selected: [],
      bulkRunning: false,
      // Stats cards config (constant)
      statsCards: STATS_CARDS,
      reasonDialog: {
        show: false,
        action: null,
        target: null,
        title: '',
        reason: '',
        error: '',
        submitting: false,
      },
      // Modal Tạo HĐ — chỉ chứa contractNumber + bank info (INSTALLMENT).
      // Pháp lý KH đã persist ở form Đề nghị (Upsert.vue).
      convertDialog: {
        show: false,
        target: null,
        contractNumber: '',
        // bankName + loanAmount: bỏ 2 field này (2026-05-14) — giữ paymentType
        // để template phân nhánh nếu sau này cần thêm logic INSTALLMENT khác.
        paymentType: null,
        submitting: false,
        errors: {},
        errorMessage: '',
      },
    };
  },
  computed: {
    bvStatusOptions() {
      return [
        { value: null, text: 'Tất cả trạng thái' },
        ...this.statusOptions.map((o) => ({ value: o.value, text: o.text })),
      ];
    },
    bvStoreOptions() {
      return [
        { value: null, text: 'Tất cả cửa hàng' },
        ...this.storeOptions.map((o) => ({
          value: o.value,
          text: o.text,
        })),
      ];
    },
    numberOfPage() {
      return Math.max(1, Math.ceil(this.total / this.size));
    },
    selectableIds() {
      return (this.items || []).map((i) => i.id).filter(Boolean);
    },
    allSelectedOnPage() {
      const ids = this.selectableIds;
      return ids.length > 0 && ids.every((id) => this.selected.includes(id));
    },
    someSelectedOnPage() {
      const ids = this.selectableIds;
      const count = ids.filter((id) => this.selected.includes(id)).length;
      return count > 0 && count < ids.length;
    },
    bulkApproveEnabled() {
      // Bulk Duyệt chỉ enable khi tất cả selected đều ở trạng thái pending.
      if (!this.selected.length) return false;
      const map = new Map(this.items.map((it) => [it.id, it]));
      return this.selected.every((id) => {
        const it = map.get(id);
        return it && it.status === CONTRACT_REQUEST_STATUS.PENDING;
      });
    },
    pageTotal() {
      return (this.items || []).reduce(
        (sum, req) => sum + this.getRequestTotal(req),
        0,
      );
    },
    pageQuantity() {
      return (this.items || []).reduce((sum, req) => {
        const items = (req && req.items) || [];
        return sum + items.reduce((s, it) => s + (Number(it.quantity) || 0), 0);
      }, 0);
    },
  },
  watch: {
    '$route.query.page'(newPage) {
      this.page = parseInt(newPage) || 1;
      this.loadList();
    },
  },
  created() {
    this.fetchStores();
    this.loadList();
    this.loadStats();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Đề nghị tạo HĐ', route: '/contract-requests' },
      { title: 'Danh sách Đề nghị' },
    ]);
  },
  methods: {
    statusLabel(status) {
      return CONTRACT_REQUEST_STATUS_LABEL[status] || status;
    },
    statusIcon(status) {
      return (STATUS_INFO_MAP[status] || {}).icon || 'fas fa-circle';
    },
    statusBadgeStyle(status) {
      const info = STATUS_INFO_MAP[status] || {};
      return {
        background: info.bg || '#F3F6F9',
        color: info.color || '#80808F',
      };
    },
    isBusiness(item) {
      return item && item.customerType === 'BUSSINESS';
    },
    getProductCode(it) {
      // Đọc productCode từ relation product (BE leftJoin items.product trong findAll).
      const code = (
        it.productCode ||
        (it.product && it.product.code) ||
        ''
      ).trim();
      return code;
    },
    /**
     * Sub-total = sum items.totalAmount (sau discount per-item, trước discount request-level).
     */
    getRequestSubtotal(req) {
      const items = (req && req.items) || [];
      // BE nest SP bán kèm (attachs) + SP tặng kèm (gifts) vào SP chính. SP bán
      // kèm CÓ giá → phải cộng vào tổng (BE: "totalAmount ĐƯỢC tính vào tổng tiền
      // Đề Nghị"); SP tặng kèm totalAmount = 0 (cộng vô hại).
      return items.reduce((sum, it) => {
        const childTotal = [...(it.attachs || []), ...(it.gifts || [])].reduce(
          (s, c) => s + (Number(c.totalAmount) || 0),
          0,
        );
        return sum + (Number(it.totalAmount) || 0) + childTotal;
      }, 0);
    },
    /**
     * Tổng tiền cuối cùng = sub-total - request-level discount.
     * Apply discountType (AMOUNT/PERCENT) qua helper `calTotalDiscount`.
     */
    getRequestTotal(req) {
      const subtotal = this.getRequestSubtotal(req);
      if (!req) return subtotal;
      const discountAmount = Number(req.totalDiscountAmount) || 0;
      if (discountAmount <= 0) return subtotal;
      return calTotalDiscount(subtotal, {
        discountType: req.discountType || 'AMOUNT',
        discountAmount,
      });
    },
    /**
     * Số tiền chiết khấu thực tế (sau khi apply discountType lên subtotal).
     * AMOUNT: trả thẳng giá trị nhập.
     * PERCENT: tính ra số tiền tương ứng.
     */
    getRequestDiscountValue(req) {
      if (!req) return 0;
      const discountAmount = Number(req.totalDiscountAmount) || 0;
      if (discountAmount <= 0) return 0;
      if (req.discountType === 'PERCENT') {
        const subtotal = this.getRequestSubtotal(req);
        return Math.round((subtotal * discountAmount) / 100);
      }
      return discountAmount;
    },
    /**
     * Trả về items[] đã chuẩn hoá — luôn ≥ 1 phần tử để rowspan hoạt động.
     * Nếu request không có item, render 1 placeholder row.
     */
    normalizedItems(req) {
      const items = (req && req.items) || [];
      if (!items.length) return [{ _placeholder: true }];
      // BE nest SP tặng kèm (gifts) + SP bán kèm (attachs) vào SP chính. Flatten
      // mỗi SP chính kéo theo gifts/attachs thành dòng riêng để list hiển thị đủ
      // (rowspan các cột dùng chung tự co theo length nên không cần sửa thêm).
      const rows = [];
      for (const main of items) {
        rows.push(main);
        for (const g of main.gifts || []) {
          rows.push({ ...g, _lineKind: 'GIFT' });
        }
        for (const a of main.attachs || []) {
          rows.push({ ...a, _lineKind: 'ATTACH' });
        }
      }
      return rows;
    },
    getCreatorName(req) {
      if (!req) return '';
      return (
        (req.userCreated && req.userCreated.fullName) || req.saleName || ''
      );
    },
    /**
     * Tên TVBH (Tư vấn bán hàng) — ưu tiên relation `userSale.fullName`,
     * fallback snapshot `saleName` (nếu có).
     */
    getSaleName(req) {
      if (!req) return '';
      return (req.userSale && req.userSale.fullName) || req.saleName || '';
    },
    formatDateOnly(value) {
      if (!value) return '-';
      const m = moment(value);
      return m.isValid() ? m.format('DD/MM/YYYY') : '-';
    },
    formatTimeOnly(value) {
      if (!value) return '';
      const m = moment(value);
      return m.isValid() ? m.format('HH:mm:ss') : '';
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return '-';
      return convertPrice(Number(value)) + ' đ';
    },
    formatDate(value) {
      if (!value) return '-';
      const m = moment(value);
      return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : '-';
    },
    canApprove(item) {
      return item.status === CONTRACT_REQUEST_STATUS.PENDING;
    },
    canReject(item) {
      return item.status === CONTRACT_REQUEST_STATUS.PENDING;
    },
    canCancel(item) {
      return [
        CONTRACT_REQUEST_STATUS.PENDING,
        CONTRACT_REQUEST_STATUS.REJECTED,
        CONTRACT_REQUEST_STATUS.APPROVED,
      ].includes(item.status);
    },
    canConvert(item) {
      return item.status === CONTRACT_REQUEST_STATUS.APPROVED;
    },
    linkGen(pageNum) {
      return pageNum === 1 ? '?' : `?page=${pageNum}`;
    },
    onPageChange(pageNum) {
      this.page = pageNum;
      // pagination-nav use-router tự update query.page → watcher trigger loadList.
    },
    onFilter() {
      this.page = 1;
      this.selected = [];
      if (this.$route.query.page && this.$route.query.page !== '1') {
        this.$router.replace({ query: { ...this.$route.query, page: 1 } });
      } else {
        this.loadList();
      }
      this.loadStats();
    },
    onSelectStat(status) {
      this.filter.status = status;
      this.onFilter();
    },
    toggleSelect(id) {
      const idx = this.selected.indexOf(id);
      if (idx === -1) this.selected.push(id);
      else this.selected.splice(idx, 1);
    },
    toggleSelectAll() {
      if (this.allSelectedOnPage) {
        // Bỏ chọn tất cả trên trang hiện tại
        this.selected = this.selected.filter(
          (id) => !this.selectableIds.includes(id),
        );
      } else {
        const next = new Set(this.selected);
        this.selectableIds.forEach((id) => next.add(id));
        this.selected = Array.from(next);
      }
    },
    clearSelection() {
      this.selected = [];
    },
    /**
     * Bulk approve — gọi API approve(id) tuần tự cho từng item.
     * Báo toast tổng kết. Nếu lỗi vài cái, vẫn approve tiếp các cái còn lại.
     */
    async bulkApprove() {
      if (!this.selected.length) return;
      const ok = window.confirm(
        `Duyệt ${this.selected.length} Đề nghị đang chọn?`,
      );
      if (!ok) return;
      this.bulkRunning = true;
      let success = 0;
      let failed = 0;
      for (const id of [...this.selected]) {
        try {
          await ContractRequestService.approve(id);
          success += 1;
        } catch {
          failed += 1;
        }
      }
      this.bulkRunning = false;
      this.selected = [];
      if (success) makeToastSuccess(`Đã duyệt ${success} đề nghị`);
      if (failed) makeToastFaile(`Có ${failed} đề nghị không duyệt được`);
      this.loadList();
      this.loadStats();
    },
    async fetchStores() {
      try {
        const { data } = await ApiService.get('stores/get-by-user');
        if (data && data.status === 1) {
          const stores = get(data, 'data.stores', []) || [];
          this.storeOptions = stores.map((s) => ({
            value: s.id,
            text: s.name || s.shortName || s.id,
          }));
        }
      } catch (err) {
        // Pre-existing store API có thể trả 200 với status khác — bỏ qua silently.
        console.error('fetchStores error', err);
      }
    },
    /**
     * Build params khoảng thời gian (theo Ngày tạo) gửi lên BE.
     * BE so sánh `cr.createdAt BETWEEN fromDate AND toDate` → toDate phải tới
     * cuối ngày (23:59:59) để không loại bỏ bản ghi tạo trong ngày Đến.
     */
    buildDateParams() {
      return {
        fromDate: this.filter.fromDate
          ? moment(this.filter.fromDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
          : undefined,
        toDate: this.filter.toDate
          ? moment(this.filter.toDate, 'DD/MM/YYYY').format(
              'YYYY-MM-DD 23:59:59',
            )
          : undefined,
      };
    },
    async loadList() {
      this.loading = true;
      try {
        const params = {
          page: this.page,
          limit: this.size,
          status: this.filter.status || undefined,
          storeId: this.filter.storeId || undefined,
          search: this.filter.customerName || undefined,
          ...this.buildDateParams(),
        };
        const res = await ContractRequestService.getAll(params);
        const payload = get(res, 'data', {});
        const data = payload.data;
        // BE shape: { data: { data: rows, paginations: { total } } } via getPaginateRes
        // Fallback handle 3 shape (xem PIT-F-003).
        if (data && Array.isArray(data.data)) {
          this.items = data.data;
          this.total = this.extractTotal(data, data.data.length);
        } else if (Array.isArray(data)) {
          this.items = data;
          this.total = data.length;
        } else if (Array.isArray(payload)) {
          this.items = payload;
          this.total = payload.length;
        } else {
          this.items = [];
          this.total = 0;
        }
      } catch (err) {
        const msg =
          get(err, 'response.data.message') ||
          err.message ||
          'Không tải được danh sách';
        makeToastFaile(msg);
        this.items = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    /**
     * Đọc total từ response BE getPaginateRes:
     *   { data: rows, paginations: { total, totalPages, currentPage } }
     * Fallback các shape khác cho an toàn.
     */
    extractTotal(data, fallback = 0) {
      if (
        data &&
        data.paginations &&
        typeof data.paginations.total === 'number'
      ) {
        return data.paginations.total;
      }
      if (data && typeof data.total === 'number') return data.total;
      return fallback;
    },
    /**
     * Load 4 stats (Tổng / Chờ duyệt / Đã duyệt / Đã tạo HĐMB) song song.
     * Mỗi call limit=1 chỉ để đọc total — rẻ hơn fetch hết.
     * Filter theo storeId hiện tại để stats khớp với filter user đang chọn.
     */
    async loadStats() {
      const baseParams = {
        page: 1,
        limit: 1,
        storeId: this.filter.storeId || undefined,
        // Stats khớp với khoảng thời gian đang lọc (đồng bộ list).
        ...this.buildDateParams(),
      };
      const calls = STATS_CARDS.map((s) => {
        const params = { ...baseParams };
        if (s.status) params.status = s.status;
        return ContractRequestService.getAll(params)
          .then((res) => {
            const payload = get(res, 'data', {});
            const data = payload.data;
            const total = this.extractTotal(
              data,
              Array.isArray(data) ? data.length : 0,
            );
            this.$set(this.statsCounts, s.key, total);
          })
          .catch(() => {
            this.$set(this.statsCounts, s.key, 0);
          });
      });
      await Promise.all(calls);
    },
    goCreate() {
      this.$router.push({ name: 'create-contract-request' });
    },
    onEdit(item) {
      this.$router.push({
        name: 'edit-contract-request',
        params: { id: item.id },
      });
    },
    /**
     * Bấm Duyệt từ list → navigate sang detail với query `action=review`
     * để user review thông tin lần cuối trước khi confirm duyệt
     * (form khoá + nút "Cập nhật" đổi thành "Duyệt"). Tránh duyệt nhầm.
     */
    onApprove(item) {
      this.$router.push({
        name: 'edit-contract-request',
        params: { id: item.id },
        query: { action: 'review' },
      });
    },
    onReject(item) {
      this.openReasonDialog(REASON_ACTION.REJECT, item, 'Từ chối Đề nghị');
    },
    onCancel(item) {
      this.openReasonDialog(REASON_ACTION.CANCEL, item, 'Hủy Đề nghị');
    },
    openReasonDialog(action, target, title) {
      this.reasonDialog = {
        show: true,
        action,
        target,
        title,
        reason: '',
        error: '',
        submitting: false,
      };
    },
    closeReasonDialog() {
      this.reasonDialog.show = false;
    },
    async submitReasonDialog() {
      const reason = (this.reasonDialog.reason || '').trim();
      if (reason.length < 10) {
        this.reasonDialog.error = 'Lý do tối thiểu 10 ký tự';
        return;
      }
      this.reasonDialog.error = '';
      this.reasonDialog.submitting = true;
      try {
        const id = this.reasonDialog.target.id;
        if (this.reasonDialog.action === REASON_ACTION.REJECT) {
          await ContractRequestService.reject(id, reason);
          makeToastSuccess('Đã từ chối');
        } else {
          await ContractRequestService.cancel(id, reason);
          makeToastSuccess('Đã hủy');
        }
        this.reasonDialog.show = false;
        this.loadList();
        this.loadStats();
      } catch (err) {
        const msg =
          get(err, 'response.data.message') ||
          err.message ||
          'Thực hiện thất bại';
        this.reasonDialog.error = msg;
      } finally {
        this.reasonDialog.submitting = false;
      }
    },
    onConvert(item) {
      // Modal Tạo HĐ chỉ chuyển trạng thái — không nhập pháp lý.
      this.convertDialog = {
        show: true,
        target: item,
        contractNumber: item.code || '',
        // bankName + loanAmount: đã bỏ khỏi modal (2026-05-14).
        paymentType: item.paymentType || null,
        submitting: false,
        errors: {},
        errorMessage: '',
      };
    },
    closeConvertDialog() {
      this.convertDialog.show = false;
    },
    /**
     * Submit:
     *  1. Validate contractNumber.
     *  2. POST /contract/from-request/:id — BE check pháp lý KH (đã persist
     *     ở form Đề nghị) → tạo HĐ + navigate sang detail V2.
     */
    async submitConvertDialog() {
      this.convertDialog.errors = {};
      this.convertDialog.errorMessage = '';

      const contractNumber = (this.convertDialog.contractNumber || '').trim();
      if (!contractNumber) {
        this.$set(
          this.convertDialog.errors,
          'contractNumber',
          'Vui lòng nhập mã HĐ',
        );
        return;
      }

      const item = this.convertDialog.target;
      this.convertDialog.submitting = true;
      try {
        // 2026-05-14: bỏ bankName + loanAmount khỏi payload (xem comment ở
        // template). BE accept missing — không còn validation require.
        const body = { contractNumber };
        const res = await ContractService.createFromRequest(item.id, body);
        const newContract = get(res, 'data.data') || get(res, 'data') || {};
        const contractId = newContract.id || newContract.contractId;
        makeToastSuccess('Đã tạo HĐ');
        this.convertDialog.show = false;
        if (contractId) {
          this.$router.push({
            name: 'edit-contract',
            params: { id: contractId },
          });
        } else {
          this.loadList();
          this.loadStats();
        }
      } catch (err) {
        this.convertDialog.errorMessage =
          get(err, 'response.data.message') || err.message || 'Tạo HĐ thất bại';
      } finally {
        this.convertDialog.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Palette match template de-nghi-lam-hd-ui.jsx
$cr-primary: #3699ff;
$cr-success: #1bc5bd;
$cr-danger: #f64e60;
$cr-warning: #ffa800;
$cr-info: #8950fc;
$cr-bg: #f3f6f9;
$cr-bg-dark: #ecf0f3;
$cr-border: #e5eaee;
$cr-text-dark: #464e5f;
$cr-text-muted: #b5b5c3;
$cr-text-light: #80808f;

.contract-request-list {
  // ===== Stats cards =====
  .cr-stats {
    display: grid;
    // 6 stat cards (Tổng / Chờ duyệt / Đã duyệt / Đã tạo HĐMB / Từ chối / Đã hủy).
    // `auto-fit` responsive: màn rộng (>1200px) → 6 card 1 hàng; trung bình
    // (1000-1200) → 3 card/hàng; mobile → 2 card/hàng. Card không bị bóp dưới 180px.
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 10px;
    margin-bottom: 12px;
  }
  .cr-stat-card {
    background: #fff;
    border: 1px solid $cr-border;
    border-radius: 6px;
    padding: 10px 14px;
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      border-color: lighten($cr-primary, 10%);
    }
    &.active {
      border-color: $cr-primary;
      background: #f8fbff;
    }
  }
  .cr-stat-num {
    min-width: 36px;
    height: 36px;
    padding: 0 10px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    font-weight: 700;
    flex-shrink: 0;
  }
  .cr-stat-label {
    font-size: 12px;
    font-weight: 500;
    color: $cr-text-light;
  }

  // ===== Filter card =====
  .cr-filter-card {
    background: #fff;
    border: 1px solid $cr-border;
    border-radius: 6px;
    padding: 12px 14px;
    margin-bottom: 12px;
  }
  .cr-label {
    font-size: 11px;
    color: $cr-text-muted;
    margin-bottom: 4px;
    display: block;
    font-weight: 500;
  }
  // 2 ô ngày (Từ/Đến) chia đều trong 1 cột, wrap khi hẹp.
  .cr-daterange {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    > * {
      flex: 1 1 110px;
      min-width: 0;
    }
  }

  // ===== Table styles — base copy từ /contracts ContractListTable =====
  .table-bordered-list th {
    border: 0.5px solid #d2d4d6 !important;
    background-color: #e0e0e0;
  }
  // Mặc định td chỉ có border 4 cạnh nhạt — KHÔNG có bottom riêng → tránh stack
  // với border-top của row kế tiếp khi border-collapse.
  .table-bordered-list td {
    border: 0.5px solid #f3f3f3;
    border-bottom: 0;
  }
  // 1 đường tách giữa các request — mỏng + nhạt.
  tbody.cr-req-group > tr:last-child > td {
    border-bottom: 0.5px solid #d8dbe0;
  }
  .tdCenter {
    vertical-align: middle;
  }
  .icon-color {
    color: #464e5f;
  }
  .header-table-color {
    color: rgb(24, 28, 50);
  }

  // Hover: highlight CẢ NHÓM (meta + tất cả item rows của 1 request) khi hover
  // bất kỳ row nào trong tbody của request đó. Fix bug rowspan hover lệch.
  .table-hover tbody.cr-req-group:hover tr td {
    background-color: #f6f8fb;
  }
  // Disable default Bootstrap row-level hover để tránh đè lên group hover trên
  .table-hover tbody.cr-req-group tr:hover td {
    background-color: #f6f8fb;
  }
  // Sum row tbody không được highlight
  .table-hover tbody.cr-sum-tbody:hover tr td {
    background-color: transparent;
  }

  // Customer cell click — orderCode pattern (copy từ /contracts ContractListTable)
  .orderCode:hover {
    text-decoration: underline;
    cursor: pointer;
  }

  // Status badge fallback (cho status không phải approved — không có approvedAt)
  .cr-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
    line-height: 1;
  }
  .cr-status-icon {
    font-size: 11px;
    // FA icon mặc định không có color → kế thừa `currentColor` từ parent
    // `.cr-status-badge` (đã set inline qua `statusBadgeStyle`). `!important`
    // để bypass rule global `i { color: ... }` của Metronic/Bootstrap nếu có
    // (đã thấy icon hiện màu xám trong khi text đúng màu purple/đỏ → có override
    // ở đâu đó).
    color: inherit !important;
  }

  // ===== Bulk action bar =====
  .cr-bulk-bar {
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: $cr-text-dark;
    color: #fff;
    padding: 10px 20px;
    border-radius: 8px;
    display: flex;
    gap: 12px;
    align-items: center;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
    z-index: 100;
  }
  .cr-bulk-count {
    font-size: 13px;
    font-weight: 500;
  }
}

// Transition for bulk bar
.cr-bulk-fade-enter-active,
.cr-bulk-fade-leave-active {
  transition: all 0.2s ease;
}
.cr-bulk-fade-enter,
.cr-bulk-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, 8px);
}
</style>
